import { DateTime } from "luxon";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { IndexedFailureMetadata } from "../../../../api/useFailureMetadata";
import type { RawFailureValues } from "../../../../api/useIndexedFailureValues";
import type { ManualFailure } from "../../../../api/useManualFailures";
import type { ProjectCategory } from "../../../../api/useProjectCategories";
import type { Quantity } from "../../../../api/useQuantities";
import type { Setup } from "../../../../api/useSetups";

// API snapshots are immutable. Weak keys share preparation across blocks without retaining
// obsolete snapshots. Only indexes are shared: dataset rows remain owned by each block.
function cacheBySource<Source extends object, Index>(build: (source: Source) => Index) {
  const cache = new WeakMap<Source, Index>();
  return (source: Source): Index => {
    const cached = cache.get(source);
    if (cached !== undefined) return cached;
    const index = build(source);
    cache.set(source, index);
    return index;
  };
}

export const getQuantitiesByName = cacheBySource(
  (quantities: Quantity[]): ReadonlyMap<string, Quantity> =>
    new Map(quantities.map(quantity => [quantity.name, quantity])),
);

export const getQuantitiesById = cacheBySource(
  (quantities: Quantity[]): ReadonlyMap<string, Quantity> =>
    new Map(quantities.map(quantity => [quantity.id, quantity])),
);

export const getEquipmentsById = cacheBySource(
  (equipments: ProjectCategory[]): ReadonlyMap<string, ProjectCategory> =>
    new Map(equipments.map(equipment => [equipment.id, equipment])),
);

export const getManualFailuresByName = cacheBySource(
  (failures: ManualFailure[]): ReadonlyMap<string, ManualFailure> =>
    new Map(failures.map(failure => [failure.name, failure])),
);

export const getDiagnosisInputsBySetup = cacheBySource(
  (setup: Setup): ReadonlyMap<string, Setup["diagnosisInputs"][number]> =>
    new Map(setup.diagnosisInputs.map(input => [input.quantityId, input])),
);

export const getSimulationOutputsBySetup = cacheBySource(
  (setup: Setup): ReadonlyMap<string, Setup["simulationOutputs"][number]> =>
    new Map(setup.simulationOutputs.map(output => [output.quantityId, output])),
);

export const getFailuresBySetup = cacheBySource(
  (setup: Setup): ReadonlyMap<string, Setup["failures"][number]> =>
    new Map(setup.failures.map(failure => [failure.name, failure])),
);

export const getFailureValuesByDateAndName = cacheBySource(
  (
    values: RawFailureValues,
  ): ReadonlyMap<number, ReadonlyMap<string, RawFailureValues[number]>> => {
    const index = new Map<number, Map<string, RawFailureValues[number]>>();
    for (const value of values) {
      const byName = index.get(value.date) ?? new Map();
      byName.set(value.failureName, value);
      index.set(value.date, byName);
    }
    return index;
  },
);

export const getFailureStatusesByProject = cacheBySource(
  (
    statuses: IndexedFailureMetadata["failureValueStatus"],
  ): ReadonlyMap<string, ReadonlyMap<number, ReadonlyMap<string, (typeof statuses)[number]>>> => {
    const index = new Map<string, Map<number, Map<string, (typeof statuses)[number]>>>();
    for (const status of statuses) {
      const byDate = index.get(status.projectId) ?? new Map();
      const byName = byDate.get(status.date) ?? new Map();
      byName.set(status.failureName, status);
      byDate.set(status.date, byName);
      index.set(status.projectId, byDate);
    }
    return index;
  },
);

export const getImpactsByFailure = cacheBySource(
  (
    failure: Setup["failures"][number],
  ): ReadonlyMap<string, readonly (typeof failure.impacts)[number][]> => {
    const index = new Map<string, (typeof failure.impacts)[number][]>();
    for (const impact of failure.impacts ?? []) {
      const impacts = index.get(impact.quantityId) ?? [];
      impacts.push(impact);
      index.set(impact.quantityId, impacts);
    }
    return index;
  },
);

const observablesBySetup = new WeakMap<
  Setup,
  WeakMap<Setup["failures"][number], Setup["diagnosisInputs"][number][]>
>();

export function getObservablesForFailure(failure: Setup["failures"][number], setup: Setup) {
  let cache = observablesBySetup.get(setup);
  if (!cache) {
    cache = new WeakMap();
    observablesBySetup.set(setup, cache);
  }
  const cached = cache.get(failure);
  if (cached) return cached;
  const inputs = getDiagnosisInputsBySetup(setup);
  const observables: Setup["diagnosisInputs"][number][] = [];
  for (const quantityId of getImpactsByFailure(failure).keys()) {
    const input = inputs.get(quantityId);
    if (input) observables.push(input);
  }
  cache.set(failure, observables);
  return observables;
}

const datePartsByAnalysis = new WeakMap<
  ActiveAnalysis,
  Map<string, { hour: number; dayOfYear: number }>
>();

export function getAnalysisDateParts(analysis: ActiveAnalysis, timezone: string) {
  let byTimezone = datePartsByAnalysis.get(analysis);
  if (!byTimezone) {
    byTimezone = new Map();
    datePartsByAnalysis.set(analysis, byTimezone);
  }
  const cached = byTimezone.get(timezone);
  if (cached) return cached;
  const dateTime = DateTime.fromMillis(analysis.date).setZone(timezone);
  const parts = { hour: dateTime.hour, dayOfYear: dateTime.ordinal };
  byTimezone.set(timezone, parts);
  return parts;
}
